import turfArea from '@turf/area'
import turfLength from '@turf/length'
import L from 'leaflet'

//Для форматирования квадратных метров
const M_NUMBER_FORMATTER = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0
})

//Для форматирования квадратных километров
const KM_NUMBER_FORMATTER = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 3
})

//Метров в километре
const M_FOR_KM = 1000
//Квадратных метров в квадратном километре
const SQUARE_M_FOR_KM = 1000000

const RULER_PANE_NAME = 'ruler'

L.Control.Ruler = L.Control.extend({
  options: {
    position: 'topright',
    toolName: 'ruler',
    mode: 'distance', //distance | area
    iconCss: 'Icon IconRuler',
    title: 'Измерение расстояния',

    cssClass: 'leaflet-ruler',
    activeCssClass: 'leaflet-ruler-clicked',

    circleMarker: {
      interactive: false,
      pane: RULER_PANE_NAME,
      color: 'red',
      radius: 2
    },
    lineStyle: {
      interactive: false,
      pane: RULER_PANE_NAME,
      color: 'red',
      dashArray: '1,6'
    }
  },

  onAdd(map) {
    this._map = map
    this._container = L.DomUtil.create('div', 'leaflet-bar')
    this._container.title = this.options.title
    this._container.classList.add(this.options.cssClass)
    L.DomUtil.create('i', this.options.iconCss, this._container)
    L.DomEvent.disableClickPropagation(this._container)
    L.DomEvent.on(this._container, 'click', this.toggle, this)
    this.active = false
    this._defaultCursor = this._map._container.style.cursor
    this._allLayers = L.layerGroup()

    //Создание pane для линейки, zIndex должен быть больше чем у pane для слоев левой панели
    if (!this._map.getPane(RULER_PANE_NAME)) {
      const rulerPane = this._map.createPane(RULER_PANE_NAME)
      rulerPane.style.zIndex = 460
    }

    return this._container
  },

  onRemove() {
    L.DomEvent.off(this._container, 'click', this.toggle, this)
    this._map.fire('ruler-end')
  },

  toggle({ silent = false }) {
    this.active = !this.active
    this._lastClickedLatLng = null
    this._clickedLatLngs = []

    if (this.active) {
      this._map.doubleClickZoom.disable()
      L.DomEvent.on(this._map._container, 'keydown', this._keyDown, this)
      L.DomEvent.on(this._map._container, 'dblclick', this._dblClicked, this)
      L.DomEvent.on(this._map._container, 'contextmenu', this._rightClick, this)
      this._container.classList.add(this.options.activeCssClass)
      this._clickCount = 0

      /**Процесс измерения */
      this._rulerInProgress = false

      /**Вершины линии/полигона */
      this._circleMarkers = L.featureGroup().addTo(this._allLayers)

      /**Слой с полигоном */
      this._poly = null

      /**Передвигаемая точка при рисовании */
      this._mouseMoveCircleMarker = null

      this._allLayers.addTo(this._map)
      this._map._container.style.cursor = 'crosshair'
      this._map.on('click', this._clicked, this)
      this._map.on('mousemove', this._moving, this)
      if (!silent) {
        this._map.fire('mouse-tool-change', {
          toolName: this.options.toolName,
          leafletControl: this
        })
      }
    } else {
      this._map.doubleClickZoom.enable()
      L.DomEvent.off(this._map._container, 'keydown', this._keyDown, this)
      L.DomEvent.off(this._map._container, 'dblclick', this._dblClicked, this)
      L.DomEvent.off(this._map._container, 'contextmenu', this._rightClick, this)
      this._container.classList.remove(this.options.activeCssClass)
      this._map.removeLayer(this._allLayers)
      this._allLayers = L.layerGroup()
      this._map._container.style.cursor = this._defaultCursor
      this._map.off('click', this._clicked, this)
      this._map.off('mousemove', this._moving, this)
      if (!silent) {
        this._map.fire('mouse-tool-change', {
          toolName: null,
          leafletControl: this
        })
      }
    }
  },

  _clicked(event) {
    if (!this._rulerInProgress) {
      //Очистить полигон прошлых измерений
      this._resetRulerLayers()
    }

    this._rulerInProgress = true
    this._lastClickedLatLng = event.latlng
    this._clickedLatLngs.push(this._lastClickedLatLng)
    L.circleMarker(this._lastClickedLatLng, this.options.circleMarker).addTo(this._circleMarkers)
    this._clickCount++
    this._map.fire('ruler-progress')
  },

  _moving(event) {
    if (!this._lastClickedLatLng) {
      return
    }

    this._movingLatLng = event.latlng
    const newLatLngs = [...this._clickedLatLngs, this._movingLatLng]
    this._updateRulerPoly(newLatLngs)
    this._updateMovingCircleMarker()
    this._showTooltip(newLatLngs, this._mouseMoveCircleMarker)
  },

  _rightClick(event) {
    if (this._clickedLatLngs.length < 2) {
      this._rulerInProgress = false
      this._resetRuler()
    } else {
      this._clickedLatLngs = this._clickedLatLngs.slice(0, this._clickedLatLngs.length - 1)
      this._updateRulerPoly([...this._clickedLatLngs, this._movingLatLng])
      this._updateMovingCircleMarker()
      this._removeLastCircleMarker()
      this._showTooltip(this._clickedLatLngs, this._mouseMoveCircleMarker)
    }

    event.preventDefault()
  },

  _showTooltip(latLngs, marker) {
    if (this.options.mode === 'distance' && latLngs.length > 1) {
      this._showDistanceTooltip(marker)
    } else if (this.options.mode === 'area' && latLngs.length > 2) {
      this._showAreaTooltip(marker)
    }
  },

  _updateRulerPoly(newLatLngs) {
    if (this._poly) {
      this._poly.setLatLngs(newLatLngs)
    } else {
      const polyFactoryFn = this.options.mode === 'area' ? L.polygon : L.polyline
      this._poly = polyFactoryFn(newLatLngs, this.options.lineStyle)
      this._poly.addTo(this._allLayers)
    }
  },

  _updateMovingCircleMarker() {
    if (this._mouseMoveCircleMarker) {
      this._mouseMoveCircleMarker.setLatLng(this._movingLatLng)
    } else {
      this._mouseMoveCircleMarker = L.circleMarker(this._movingLatLng, this.options.circleMarker)
      this._mouseMoveCircleMarker.addTo(this._allLayers)
    }
  },

  _dblClicked() {
    this._rulerInProgress = false
    this._removeLastCircleMarker()
    this._closePath(this._clickedLatLngs.slice(0, this._clickedLatLngs.length - 1))
  },

  _removeLastCircleMarker() {
    const circleMarkers = this._circleMarkers.getLayers()
    if (circleMarkers.length <= 0) {
      return
    }

    const lastCircleMarker = circleMarkers[circleMarkers.length - 1]
    if (lastCircleMarker) {
      lastCircleMarker.removeFrom(this._circleMarkers)
    }
  },

  _keyDown(event) {
    switch (event.keyCode) {
      case 13:
        this._closePathOnEnter()
        break
      case 27:
        this._rulerInProgress = false
        this._resetRuler()
        break
    }
  },

  _closePathOnEnter() {
    this._rulerInProgress = false
    L.circleMarker(this._movingLatLng, this.options.circleMarker).addTo(this._circleMarkers)
    this._closePath([...this._clickedLatLngs, this._movingLatLng])
  },

  /**
   * Завершение рисования линии/полигона измерения площади
   * @param {*} resultLatLngs
   */
  _closePath(resultLatLngs) {
    //Сброс координат, которые запоминались для рисования
    this._resetRulerLatLngs()

    if (this._poly) {
      this._poly.setLatLngs(resultLatLngs)
      const circleMarkers = this._circleMarkers.getLayers()
      if (this.options.mode === 'area') {
        this._showAreaTooltip(circleMarkers[circleMarkers.length - 1])
      } else {
        this._showDistanceTooltip(circleMarkers[circleMarkers.length - 1])
      }
    }

    this._map.fire('ruler-end')
  },

  _showDistanceTooltip(markerForTooltip) {
    const polylineLength = turfLength(this._poly.toGeoJSON(8), { units: 'meters' })
    const polylineLengthString =
      polylineLength < M_FOR_KM
        ? M_NUMBER_FORMATTER.format(polylineLength).replace(',', '.') + ' м'
        : KM_NUMBER_FORMATTER.format(polylineLength / M_FOR_KM).replace(',', '.') + ' км'
    const tooltipText = `<b>Расстояние:&nbsp;</b>${polylineLengthString}`
    this._openTooltip(tooltipText, markerForTooltip)
  },

  /**
   * Отображение подсказки с результатом измерений
   * @param {*} markerForTooltip
   */
  _showAreaTooltip(markerForTooltip) {
    const polygonArea = turfArea(this._poly.toGeoJSON(8))
    const polygonAreaString =
      polygonArea < SQUARE_M_FOR_KM
        ? M_NUMBER_FORMATTER.format(polygonArea).replace(',', '.') + ' м<sup>2</sup>'
        : KM_NUMBER_FORMATTER.format(polygonArea / SQUARE_M_FOR_KM).replace(',', '.') + ' км<sup>2</sup>'

    const tooltipText = `<b>Площадь:&nbsp;</b>${polygonAreaString}`
    this._openTooltip(tooltipText, markerForTooltip)
  },

  _openTooltip(tooltipText, markerForTooltip) {
    markerForTooltip
      .bindTooltip(tooltipText, {
        permanent: true,
        offset: L.point(10, -10),
        className: 'moving-tooltip'
      })
      .openTooltip()
  },

  /**
   * Сброс измерений
   */
  _resetRuler() {
    this._resetRulerLatLngs()
    this._resetRulerLayers()
  },

  /**
   * Очистка координат которые запоминаются при рисовании линейки
   */
  _resetRulerLatLngs() {
    this._lastClickedLatLng = null
    this._clickedLatLngs = []
    if (this._mouseMoveCircleMarker) {
      this._mouseMoveCircleMarker.remove()
      this._mouseMoveCircleMarker = null
    }
  },

  /**
   * Очистка точек и линии/полигона, которые рисуются при измерении
   */
  _resetRulerLayers() {
    //Очистка линии/полигона
    if (this._poly) {
      this._poly.removeFrom(this._allLayers)
      this._poly = null
    }
    //Очистка вершин
    if (this._circleMarkers) {
      this._circleMarkers.removeFrom(this._allLayers)
      this._circleMarkers = L.featureGroup().addTo(this._allLayers)
    }
  }
})

L.control.ruler = function(options) {
  return new L.Control.Ruler(options)
}



// WEBPACK FOOTER //
// ./src/helpers/leaflet-ruler.js